/**
 * Extracts the responseBody.error object from a thrown service error.
 * The service layer (clientApiService.assertResponseHasNoErrors) throws an
 * Error with .responseBody containing the full API response. This function
 * safely navigates that shape.
 */
function extractResponseError(error: unknown): Record<string, unknown> | undefined {
    const responseBody = error != null && typeof error === 'object'
        && 'responseBody' in error
        ? (error as Record<string, unknown>).responseBody as
            Record<string, unknown> | undefined
        : undefined;
    const responseError = responseBody?.error;
    if (responseError != null && typeof responseError === 'object') {
        return responseError as Record<string, unknown>;
    }
    return undefined;
}

/**
 * Extracts a structured error from an unknown thrown value.
 * Validates errorCategory is one of the expected categories and checks errorCode
 * against the provided set of known codes. Falls back to 'UNKNOWN_ERROR'.
 */
export function parseApiError<T extends string>(
    error: unknown,
    options: {
        expectedCategories: readonly string[];
        knownCodes: ReadonlySet<T>;
    },
): { errorCode: T | 'UNKNOWN_ERROR'; message: string; unrecognizedCode?: string } {
    const responseError = extractResponseError(error);
    const rawCategory = responseError?.errorCategory;
    const hasValidCategory = typeof rawCategory === 'string'
        && options.expectedCategories.includes(rawCategory);
    const rawCode = hasValidCategory
        && typeof responseError?.errorCode === 'string'
        ? responseError.errorCode
        : undefined;
    let errorCode: T | 'UNKNOWN_ERROR';
    let unrecognizedCode: string | undefined;
    if (rawCode != null && options.knownCodes.has(rawCode as T)) {
        errorCode = rawCode as T;
    } else {
        errorCode = 'UNKNOWN_ERROR';
        if (rawCode != null) {
            unrecognizedCode = rawCode;
        }
    }
    let message: string;
    if (error instanceof Error) {
        message = error.message;
    } else {
        try {
            message = JSON.stringify(error);
        } catch {
            message = String(error);
        }
    }
    return { errorCode, message, unrecognizedCode };
}
